<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'

const props = withDefaults(defineProps<{
  modelValue: boolean
  title: string
  subtitle?: string
  maxWidth?: string | number
  embedded?: boolean
  persistent?: boolean
  busy?: boolean
  loading?: boolean
  draft?: unknown
  actions?: { key: string; label: string; icon: string; active?: boolean }[]
}>(), { maxWidth: 1000, actions: () => [] })
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  action: [key: string]
}>()
const { mdAndUp, xs } = useDisplay()
const isEmbedded = computed(() => props.embedded && mdAndUp.value)
const contentClass = computed(() => isEmbedded.value
  ? 'entity-form-embedded entity-form-embedded--no-chat mentorship-dialog mentorship-dialog--embedded'
  : 'entity-form-create-dialog mentorship-dialog')
const overlay = ref<{ globalTop: boolean }>()
const baseline = ref('')
const discardOpen = ref(false)
let resolveDiscard: ((value: boolean) => void) | undefined
const snapshot = () => JSON.stringify(props.draft ?? null)
function markSaved() {
  baseline.value = snapshot()
}
watch([() => props.modelValue, () => props.draft != null], markSaved, { immediate: true })
function confirmDismiss(): Promise<boolean> {
  if (props.busy) return Promise.resolve(false)
  if (snapshot() === baseline.value) return Promise.resolve(true)
  if (resolveDiscard) return Promise.resolve(false)
  discardOpen.value = true
  return new Promise(resolve => {
    resolveDiscard = resolve
  })
}
function resolveConfirmation(discard: boolean) {
  if (discard) markSaved()
  discardOpen.value = false
  resolveDiscard?.(discard)
  resolveDiscard = undefined
}
async function close() {
  if (await confirmDismiss()) emit('update:modelValue', false)
}
onBeforeUnmount(() => resolveDiscard?.(false))
defineExpose({ close, confirmDismiss, markSaved })
const guardedDismiss = useDialogDismissGuard(() => {
  if (!props.persistent) close()
})
function dismiss(event?: Event) {
  if (event?.target instanceof Element && event.target.closest('.entity-form-actions')) return
  if (!overlay.value?.globalTop || props.persistent) return
  if (isEmbedded.value) guardedDismiss(event)
  else close()
}
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue && isEmbedded" class="entity-form-backdrop" />
  </Teleport>
  <VDialog
    ref="overlay"
    :model-value="modelValue"
    :max-width="isEmbedded ? undefined : maxWidth"
    :fullscreen="xs"
    :content-class="contentClass"
    :z-index="embedded ? 2400 : 2500"
    :scrim="!isEmbedded"
    persistent
    scrollable
    @click:outside="dismiss"
    @keydown.esc.stop="dismiss"
    @update:model-value="!$event && dismiss()"
  >
    <Teleport to="body">
      <div v-if="modelValue && isEmbedded" class="entity-form-actions">
        <VTooltip
          v-for="action in actions"
          :key="action.key"
          :text="action.label"
          location="bottom"
        >
          <template #activator="{ props: tooltipProps }">
            <VBtn
              v-bind="tooltipProps"
              icon
              color="primary"
              class="entity-form-action-btn"
              :aria-label="action.label"
              :aria-pressed="action.active"
              @click="emit('action', action.key)"
            >
              <span class="history-toggle-icon">
                <VIcon :icon="action.icon" />
                <span v-if="action.active" class="history-toggle-slash" aria-hidden="true" />
              </span>
            </VBtn>
          </template>
        </VTooltip>
        <VBtn
          icon="ri-close-line"
          color="primary"
          class="entity-form-action-btn"
          aria-label="Закрыть"
          :disabled="busy"
          @click="close"
        />
      </div>
    </Teleport>
    <VCard class="entity-form-card mentorship-dialog-card">
      <div class="entity-form-pane mentorship-dialog-pane">
        <VCardTitle class="mentorship-dialog-header d-flex align-start ga-3 pa-6" :class="{ 'mentorship-dialog-header--tools': actions.length }">
          <div class="mentorship-dialog-heading flex-grow-1">
            <h2 class="text-h5">
              {{ title }}
            </h2>
            <div v-if="subtitle" class="text-body-2 text-medium-emphasis mt-1">
              {{ subtitle }}
            </div>
            <slot name="description" />
          </div>
          <div class="mentorship-dialog-header-actions d-flex flex-wrap align-center justify-end ga-2">
            <slot name="header-actions" />
            <template v-if="!isEmbedded">
              <VTooltip
                v-for="action in actions"
                :key="action.key"
                :text="action.label"
                location="bottom"
              >
                <template #activator="{ props: tooltipProps }">
                  <VBtn
                    v-bind="tooltipProps"
                    icon
                    variant="text"
                    color="primary"
                    :aria-label="action.label"
                    :aria-pressed="action.active"
                    @click="emit('action', action.key)"
                  >
                    <span class="history-toggle-icon">
                      <VIcon :icon="action.icon" />
                      <span v-if="action.active" class="history-toggle-slash" aria-hidden="true" />
                    </span>
                  </VBtn>
                </template>
              </VTooltip>
              <CustomCloseButton :close-action="close" :disabled="busy" aria-label="Закрыть" />
            </template>
          </div>
        </VCardTitle>
        <VProgressLinear v-if="loading" indeterminate class="flex-shrink-0" />
        <slot />
      </div>
    </VCard>
  </VDialog>
  <ConfirmDialog
    :is-dialog-visible="discardOpen"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Внесённые изменения не будут сохранены."
    confirm-btn="Закрыть без сохранения"
    reject-btn="Продолжить заполнение"
    @confirm="resolveConfirmation"
    @update:is-dialog-visible="!$event && resolveConfirmation(false)"
  />
</template>

<style scoped>
.mentorship-dialog-card,
.mentorship-dialog-pane {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  overflow: hidden;
}
.mentorship-dialog-heading { flex-basis: 0; min-inline-size: 0; overflow-wrap: anywhere; }
.mentorship-dialog-header { flex-shrink: 0; white-space: normal; }
.mentorship-dialog-header-actions { flex-shrink: 0; }
.mentorship-dialog-pane :deep(> .v-card-text) {
  flex: 1 1 auto;
  min-block-size: 0;
  overflow: auto;
  padding: 24px;
}
.mentorship-dialog-pane :deep(> .v-card-text > .v-input:not(:last-child)) { margin-block-end: 16px; }
.mentorship-dialog-pane :deep(> .v-card-actions) {
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  padding: 24px;
}
.mentorship-dialog-pane :deep(> .v-tabs),
.mentorship-dialog-pane :deep(> .v-stepper) { flex: 0 0 auto; }
:global(.mentorship-dialog--embedded) {
  inline-size: min(1180px, calc(100vw - 50px)) !important;
  min-inline-size: 0 !important;
}
@media (max-width: 599px) {
  .mentorship-dialog-header { padding: 16px !important; flex-wrap: wrap; }
  .mentorship-dialog-header-actions { margin-inline-start: auto; }
  .mentorship-dialog-header--tools .mentorship-dialog-header-actions,
  .mentorship-dialog-header-actions:has(.general-button) { order: -1; inline-size: 100%; }
  .mentorship-dialog-pane :deep(> .v-card-text),
  .mentorship-dialog-pane :deep(> .v-card-actions) { padding: 16px; }
}
</style>
